import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Downloads a media file whole into memory and hands out a blob: URL.
 *
 * Browsers only buffer a little of a streamed video ahead of the playhead, so
 * on a poor connection playback keeps stopping to load. With the whole file in
 * memory it plays (and seeks) without touching the network again. Files above
 * the memory budget are left to stream as before.
 */

/** Memory to spend on whole downloaded files: more on devices that report more RAM. */
export function memoryBudget(): number {
  const gb = (navigator as { deviceMemory?: number }).deviceMemory ?? 4;
  const coarse = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
  return Math.min(coarse ? 400e6 : 1.5e9, gb * (coarse ? 100e6 : 200e6));
}

interface Entry {
  url: string | null;
  bytes: number;
  loaded: number;
  total: number;
  done: Promise<string | null>;
  abort: AbortController;
  users: number;
  lastUsed: number;
  listeners: Set<() => void>;
}

const entries = new Map<string, Entry>();

async function download(src: string, e: Entry, limit: number): Promise<string | null> {
  try {
    const res = await fetch(src, { signal: e.abort.signal });
    if (!res.ok || !res.body) return null;
    e.total = Number(res.headers.get('content-length') ?? 0);
    if (e.total > limit) {
      e.abort.abort();
      return null;
    }
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let last = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      e.loaded += value.byteLength;
      if (e.loaded > limit) {
        e.abort.abort();
        return null;
      }
      const now = performance.now();
      if (now - last > 200) {
        last = now;
        for (const l of e.listeners) l();
      }
    }
    e.bytes = e.loaded;
    e.url = URL.createObjectURL(new Blob(chunks as BlobPart[], { type: res.headers.get('content-type') ?? 'video/mp4' }));
    return e.url;
  } catch {
    return null;
  } finally {
    for (const l of e.listeners) l();
  }
}

/** Frees the least recently used finished downloads nobody is watching until the rest fit the budget. */
function trim() {
  const budget = memoryBudget();
  let bytes = 0;
  for (const e of entries.values()) bytes += e.url ? e.bytes : e.loaded;
  if (bytes <= budget) return;
  const idle = [...entries.entries()].filter(([, e]) => e.users === 0).sort((a, b) => a[1].lastUsed - b[1].lastUsed);
  for (const [key, e] of idle) {
    if (bytes <= budget) break;
    bytes -= e.url ? e.bytes : e.loaded;
    drop(key, e);
  }
}

function drop(key: string, e: Entry) {
  e.abort.abort();
  if (e.url) URL.revokeObjectURL(e.url);
  entries.delete(key);
}

/**
 * Starts (or joins) the whole download of `src`, cached under `key` (signed URLs
 * change between page loads; the key names the file). Call the returned
 * release once the file is no longer shown; it stays cached while memory allows.
 */
export function holdWholeFile(key: string, src: string): { entry: Entry; release: () => void } {
  let e = entries.get(key);
  if (!e) {
    const fresh: Entry = { url: null, bytes: 0, loaded: 0, total: 0, done: Promise.resolve(null), abort: new AbortController(), users: 0, lastUsed: performance.now(), listeners: new Set() };
    // Leave room for what is already held.
    let held = 0;
    for (const o of entries.values()) if (o.users > 0) held += o.url ? o.bytes : Math.max(o.total, o.loaded);
    fresh.done = download(src, fresh, Math.max(0, memoryBudget() - held)).then((url) => {
      // Failed or too big: forget it so a later visit can try again.
      if (!url && entries.get(key) === fresh) entries.delete(key);
      return url;
    });
    entries.set(key, fresh);
    e = fresh;
  }
  const entry = e;
  entry.users++;
  entry.lastUsed = performance.now();
  trim();
  let released = false;
  return {
    entry,
    release: () => {
      if (released) return;
      released = true;
      entry.users--;
      entry.lastUsed = performance.now();
      // Nobody needs an unfinished download any more: stop spending the connection on it.
      if (entry.users === 0 && !entry.url && entries.get(key) === entry) drop(key, entry);
      trim();
    },
  };
}

/** The in-memory copy of a file, if it has finished downloading. */
export function wholeFileUrl(key: string): string | null {
  const e = entries.get(key);
  if (!e?.url) return null;
  e.lastUsed = performance.now();
  return e.url;
}

/**
 * For a <video>/<audio>: the src to give it. That is the network URL until the
 * whole file is in memory, then its blob: URL; the element carries on from the
 * same time, rate and play state when it switches. `progress` is 0..1 while the
 * file downloads and null when it is in memory or will stream instead.
 */
export function useWholeFile(
  ref: RefObject<HTMLMediaElement | null>,
  src: string | undefined,
  key: string | undefined = src,
): { src: string | undefined; progress: number | null; inMemory: boolean } {
  const [mem, setMem] = useState<{ key: string; url: string } | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const resume = useRef<{ t: number; paused: boolean; rate: number } | null>(null);

  useEffect(() => {
    if (!src || !key) {
      setProgress(null);
      return;
    }
    const ready = wholeFileUrl(key);
    const { entry, release } = holdWholeFile(key, src);
    if (ready) {
      setMem({ key, url: ready });
      setProgress(null);
      return release;
    }
    let live = true;
    const tick = () => live && !entry.url && setProgress(entry.total ? Math.min(1, entry.loaded / entry.total) : 0);
    entry.listeners.add(tick);
    tick();
    void entry.done.then((url) => {
      if (!live) return;
      setProgress(null);
      if (!url) return;
      const el = ref.current;
      resume.current = el && el.readyState > 0 ? { t: el.currentTime, paused: el.paused, rate: el.playbackRate } : null;
      setMem({ key, url });
    });
    return () => {
      live = false;
      entry.listeners.delete(tick);
      release();
    };
  }, [src, key, ref]);

  // Already in memory (e.g. back on a version seen before): start from it straight away.
  const memUrl = (mem && mem.key === key ? mem.url : null) ?? (key ? wholeFileUrl(key) : null);
  useEffect(() => {
    const el = ref.current;
    const r = resume.current;
    if (!el || !memUrl || !r) return;
    resume.current = null;
    const restore = () => {
      el.currentTime = r.t;
      el.playbackRate = r.rate;
      if (!r.paused) void el.play().catch(() => {});
    };
    if (el.readyState > 0) restore();
    else el.addEventListener('loadedmetadata', restore, { once: true });
    return () => el.removeEventListener('loadedmetadata', restore);
  }, [memUrl, ref]);

  return { src: memUrl ?? src, progress: memUrl ? null : progress, inMemory: !!memUrl };
}
